import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeftRight,
  CheckCircle2,
  GitCompareArrows,
  Trophy,
} from "lucide-react";

import AdminHeader from "@/components/admin/AdminHeader";
import { useAuth } from "@/contexts/AuthContext";
import { getConfiguredSeason } from "@/lib/season";
import {
  deleteAdminDoubleMeetingBonus,
  getAdminDoubleMeetingBonus,
  upsertAdminDoubleMeetingBonus,
} from "@/api/adminMatches";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { formatSvDateTime } from "@/utils/date";

function formatDateTime(iso) {
  if (!iso) return "-";
  return formatSvDateTime(iso, { dateStyle: "short", timeStyle: "short" }) || "-";
}

function AggregateLine({ item }) {
  return (
    <div className="text-sm text-muted-foreground">
      Aggregate: {(item?.teams || [])
        .map((team) => `${team?.name || "-"} ${item?.totals?.[team?.id] ?? 0}`)
        .join(" • ")}
    </div>
  );
}

export default function AdminDoubleMeetingBonusPage() {
  const { user } = useAuth();
  const [season, setSeason] = useState(getConfiguredSeason());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const seasonOptions = useMemo(() => {
    const baseSeason = Number(getConfiguredSeason());
    return [baseSeason + 1, baseSeason, baseSeason - 1, baseSeason - 2, baseSeason - 3]
      .filter((value, index, array) => array.indexOf(value) === index)
      .map(String);
  }, []);

  const unresolvedItems = useMemo(
    () => items.filter((item) => item?.requires_manual_resolution),
    [items]
  );

  const resolvedItems = useMemo(
    () => items.filter((item) => item?.complete && !item?.requires_manual_resolution),
    [items]
  );

  const autoResolvedCount = useMemo(
    () => resolvedItems.filter((item) => item?.effective_source === "auto").length,
    [resolvedItems]
  );

  useEffect(() => {
    if (user?.role !== "super_admin") return;

    let mounted = true;
    setLoading(true);
    getAdminDoubleMeetingBonus(season)
      .then((res) => {
        if (!mounted) return;
        setItems(Array.isArray(res?.items) ? res.items : []);
      })
      .catch((err) => {
        toast.error(err?.message || "Kunde inte hämta bonuspoäng för dubbelmöten");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [season, user?.role]);

  if (user?.role !== "super_admin") {
    return <Navigate to="/admin" replace />;
  }

  const refreshItems = async () => {
    const res = await getAdminDoubleMeetingBonus(season);
    setItems(Array.isArray(res?.items) ? res.items : []);
  };

  const handleSetWinner = async (item, winnerTeamId) => {
    if (saving) return;
    setSaving(true);
    try {
      await upsertAdminDoubleMeetingBonus({
        season: Number(season),
        pair_key: String(item?.pair_key || ""),
        winner_team_id: String(winnerTeamId || ""),
        note: item?.is_tied
          ? "Manuellt satt bonusvinnare efter skiljeheat eller officiellt beslut."
          : "Manuell bonusoverride.",
      });
      await refreshItems();
      toast.success("Bonusvinnaren sparades.");
    } catch (err) {
      toast.error(err?.message || "Kunde inte spara bonusvinnaren");
    } finally {
      setSaving(false);
    }
  };

  const handleClearWinner = async (item) => {
    if (saving) return;
    setSaving(true);
    try {
      await deleteAdminDoubleMeetingBonus(Number(season), String(item?.pair_key || ""));
      await refreshItems();
      toast.success("Manuell bonusoverride togs bort.");
    } catch (err) {
      toast.error(err?.message || "Kunde inte rensa bonusoverride");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen">
      <AdminHeader />
      <main className="container mx-auto px-4 py-6">
        <div className="space-y-6">
          <Card className="overflow-hidden border-border/70 shadow-sm">
            <CardHeader className="relative gap-4 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white">
              <div className="absolute inset-y-0 right-0 w-32 bg-[radial-gradient(circle_at_top,rgba(245,158,11,0.24),transparent_65%)]" />
              <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                      <GitCompareArrows className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-white sm:text-2xl">
                        Bonuspoäng dubbelmöten
                      </CardTitle>
                      <CardDescription className="mt-1 max-w-2xl text-slate-200">
                        Hantera bonuspoäng för hemma- och bortamöten, och sätt vinnare manuellt när aggregate slutar lika.
                      </CardDescription>
                    </div>
                  </div>
                </div>

                <div className="grid gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 sm:min-w-[170px]">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300">
                    Säsong
                  </div>
                  <Select value={String(season)} onValueChange={(value) => setSeason(Number(value))}>
                    <SelectTrigger className="border-white/10 bg-white/10 text-white">
                      <SelectValue placeholder="Säsong" />
                    </SelectTrigger>
                    <SelectContent className="border bg-card shadow-lg">
                      {seasonOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
          </Card>

          <section className="grid gap-4 md:grid-cols-3">
            <Card className="border-border/70 shadow-sm">
              <CardContent className="px-5 py-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Kräver beslut
                </div>
                <div className="mt-2 text-2xl font-semibold text-foreground">
                  {unresolvedItems.length}
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/70 shadow-sm">
              <CardContent className="px-5 py-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Dubbelmöten
                </div>
                <div className="mt-2 text-2xl font-semibold text-foreground">{items.length}</div>
              </CardContent>
            </Card>
            <Card className="border-border/70 shadow-sm">
              <CardContent className="px-5 py-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Automatiskt avgjorda
                </div>
                <div className="mt-2 text-2xl font-semibold text-foreground">{autoResolvedCount}</div>
              </CardContent>
            </Card>
          </section>

          <Card className="border-border/70 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <ArrowLeftRight className="h-4 w-4 text-primary" />
                <CardTitle>Dubbelmöten som kräver beslut</CardTitle>
              </div>
              <CardDescription>
                Här visas bara de par där Lapstr inte kan avgöra bonusvinnaren automatiskt.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-muted-foreground">Hämtar dubbelmöten...</p>
              ) : unresolvedItems.length ? (
                <Accordion type="single" collapsible className="w-full">
                  {unresolvedItems.map((item) => (
                    <AccordionItem key={item.pair_key} value={String(item.pair_key)} className="border-b-0">
                      <div className="mb-3 overflow-hidden rounded-2xl border border-amber-500/30 bg-amber-500/5">
                        <AccordionTrigger className="px-4 py-4 no-underline hover:no-underline">
                          <div className="min-w-0 text-left">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge className="bg-amber-500 text-white hover:bg-amber-500">
                                Kräver manuell bonus
                              </Badge>
                              <span className="text-sm font-semibold text-foreground">
                                {(item?.teams || []).map((team) => team?.name || "-").join(" vs ")}
                              </span>
                            </div>
                            <div className="mt-2">
                              <AggregateLine item={item} />
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="space-y-4">
                            <div className="space-y-2">
                              {(item?.matches || []).map((match) => (
                                <div key={match.id} className="rounded-xl border border-border/70 bg-card px-3 py-3 text-sm">
                                  <div className="font-medium text-foreground">
                                    {match.home_team} - {match.away_team}
                                  </div>
                                  <div className="mt-1 text-muted-foreground">
                                    {formatDateTime(match.date)} • {match.home_score} - {match.away_score}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="grid gap-2 sm:grid-cols-2">
                              {(item?.teams || []).map((team) => (
                                <Button
                                  key={team.id}
                                  type="button"
                                  variant="outline"
                                  disabled={saving}
                                  onClick={() => handleSetWinner(item, team.id)}
                                >
                                  Ge bonus till {team.name}
                                </Button>
                              ))}
                            </div>
                          </div>
                        </AccordionContent>
                      </div>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="rounded-2xl border border-dashed border-border/70 bg-muted/20 px-4 py-5 text-sm text-muted-foreground">
                  Inga dubbelmöten kräver manuell bonushantering just nu.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/70 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <CardTitle>Avgjorda dubbelmöten</CardTitle>
              </div>
              <CardDescription>
                Kompakt historik över dubbelmöten där bonusvinnaren redan är avgjord automatiskt eller manuellt.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-muted-foreground">Hämtar bonushistorik...</p>
              ) : resolvedItems.length ? (
                <Accordion type="single" collapsible className="w-full">
                  {resolvedItems.map((item) => {
                    const winner = (item?.teams || []).find(
                      (team) => String(team?.id || "") === String(item?.effective_winner_team_id || "")
                    );

                    return (
                      <AccordionItem key={item.pair_key} value={String(item.pair_key)} className="border-b-0">
                        <div className="mb-3 overflow-hidden rounded-2xl border border-border/70 bg-card">
                          <AccordionTrigger className="px-4 py-4 no-underline hover:no-underline">
                            <div className="min-w-0 text-left">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-semibold text-foreground">
                                  {(item?.teams || []).map((team) => team?.name || "-").join(" vs ")}
                                </span>
                                <Badge variant="outline">
                                  {item?.effective_source === "manual" ? "Manuell bonus" : "Automatisk bonus"}
                                </Badge>
                              </div>
                              <div className="mt-2 space-y-1">
                                <AggregateLine item={item} />
                                <div className="flex items-center gap-2 text-sm text-foreground">
                                  <Trophy className="h-3.5 w-3.5 text-amber-500" />
                                  <span className="font-medium">{winner?.name || "Ingen vinnare satt"}</span>
                                </div>
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="space-y-4">
                              <div className="space-y-2">
                                {(item?.matches || []).map((match) => (
                                  <div key={match.id} className="rounded-xl border border-border/70 bg-muted/10 px-3 py-3 text-sm">
                                    <div className="font-medium text-foreground">
                                      {match.home_team} - {match.away_team}
                                    </div>
                                    <div className="mt-1 text-muted-foreground">
                                      {formatDateTime(match.date)} • {match.home_score} - {match.away_score}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {item?.effective_source === "manual" ? (
                                <div className="flex justify-start">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    disabled={saving}
                                    onClick={() => handleClearWinner(item)}
                                  >
                                    Rensa manuell override
                                  </Button>
                                </div>
                              ) : null}
                            </div>
                          </AccordionContent>
                        </div>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              ) : (
                <div className="rounded-2xl border border-dashed border-border/70 bg-muted/10 px-4 py-5 text-sm text-muted-foreground">
                  Inga färdiga dubbelmöten hittades för vald säsong ännu.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/70 shadow-sm">
            <CardContent className="flex flex-col gap-3 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-medium text-foreground">Tillbaka till adminöversikt</div>
                <div className="text-sm text-muted-foreground">
                  Startsidan är nu renare, medan bonushanteringen ligger samlad här.
                </div>
              </div>
              <Button asChild variant="outline">
                <Link to="/admin">Öppna översikt</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
